<template>
  <label
    v-if="labelText"
    class="sc3-form-label"
    :class="{
      'sc3-form-label-inline': isInline,
      'sc3-form-label-small': isInline
    }"
    :for="htmlId"
  >{{ labelText }}</label>
  <br v-if="labelText" />
  <select
    :id="htmlId"
    class="sc3-select-menu"
    :class="{
      'sc3-select-menu-inline': isInline,
      'sc3-select-menu-compact': isCompact,
      'sc3-select-menu-white-and-grey': isWhiteAndGrey
    }"
    v-model="period"
    @change="handlePeriodMenuChanged($event)"
  >
    <template
      v-for="periodOption in periodOptionsArray"
      :key="periodOption.value"
    >
      <option
        v-if="periodOption.dividerAbove && !shouldAutomaticallyDisableIntradayPeriodOptions"
        disabled
      >&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;</option>
      <option
        :value="periodOption.value"
        :disabled="shouldAutomaticallyDisableIntradayPeriodOptions && periodOption.isIntraday"
      >{{ periodOption.title }}</option>
      <option
        v-if="periodOption.dividerBelow && !shouldAutomaticallyDisableIntradayPeriodOptions"
        disabled
      >&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;&#9472;</option>
    </template>
  </select>
</template>

<script lang="ts" setup>
  import { ref, toRefs, computed, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useChartStore } from '@/stores/chartStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useCryptoSymbolCheck } from '@/composables/useCryptoSymbolCheck';
  import { useModals } from '@/composables/useModals';
  import { PERIOD_OPTIONS_ARRAY, getPeriodOptionFromPeriodValue } from '@/enums/period-options';

  const chartStore = useChartStore();
  const { period: chartStorePeriod } = storeToRefs(chartStore);
  const { isLoggedIn, isPro } = useAuthentication();
  const { isViewingCryptoSymbol } = useCryptoSymbolCheck();
  const { launchCreateAccountModal, launchUpgradeToProModal } = useModals();
  const props = defineProps<{
    isInline: boolean;
    labelText: string;
    htmlId: string;
    shouldAutomaticallyDisableIntradayPeriodOptions: boolean;
    isCompact: boolean;
    isWhiteAndGrey: boolean;
  }>();
  const {
    isInline,
    labelText,
    htmlId,
    shouldAutomaticallyDisableIntradayPeriodOptions,
    isCompact,
    isWhiteAndGrey
  } = toRefs(props);
  const period = ref(chartStorePeriod.value);

  const periodOptionsArray = computed(() => {
    return PERIOD_OPTIONS_ARRAY.filter(periodOption => (isViewingCryptoSymbol.value && !periodOption.isNonCryptoOnly) || (!isViewingCryptoSymbol.value && !periodOption.isCryptoOnly));
  });

  function handlePeriodMenuChanged(e: Event) {
    if (!(e.target instanceof HTMLSelectElement)) {
      return;
    }

    const periodValue = e.target.value;
    const periodOption = getPeriodOptionFromPeriodValue(periodValue);

    if (!isLoggedIn.value && periodOption.isPayingMemberOnly) {
      syncPeriod();
      launchCreateAccountModal();
      return;
    }

    if (!isPro.value && periodOption.isProOnly) {
      syncPeriod();
      launchUpgradeToProModal();
      return;
    }

    chartStorePeriod.value = periodValue;
  }

  function syncPeriod() {
    period.value = chartStorePeriod.value;
  }

  watch(chartStorePeriod, syncPeriod);
</script>
